import { datadogRum } from "@datadog/browser-rum";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import {
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation, useParams } from "react-router-dom";

import { usePagination } from "hooks";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { setAiPromptDetails } from "components/word-processor-field/word-processor.slice";
import { REPORT_TYPE_AUDIOLOGY } from "constants/audiology";
import {
  AUDIOLOGY_REPORT_DETAIL_TAG_SCOPE,
  MULTI_PAGE_AUDIOLOGY_REPORT,
  NOAH_SESSION_PICKER_NEW_REPORT
} from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentUser
} from "features/auth/auth-slice";
import useClinicNotifications from "hooks/use-clinic-notifications";
import { mergeTagsApi } from "services/merge-tags";
import {
  GET_LIST_OF_REPORTS_DEFAULT_QUERY,
  GET_REPORT_DEFAULT_QUERY,
  REPORT_LIST_NOAH_SESSION_FIELDS,
  REPORT_NOAH_SESSION_FIELDS,
  reportsApi,
  useAddCustomTestMutation,
  useAddReportMutation,
  useAutoCompleteReportSummaryMutation,
  useGetReportCustomTestsQuery,
  useGetReportQuery,
  useGetReportsQuery,
  useLazyGetReportQuery,
  useRemoveCustomTestMutation,
  useSyncReportFromNoahMutation,
  useUpdateCustomTestMutation,
  useUpdateReportMutation,
  withNoahSessionFields
} from "services/reports";
import { AudiometerConfiguration } from "services/types/audiometer-configuration.type";
import { Audiometer } from "services/types/audiometers.type";
import type { PromptOutputFormat } from "services/types/prompts.type";
import {
  type CustomTest,
  type CustomTestInput,
  type Report,
  type ReportFilters,
  type ReportResponse
} from "services/types/reports.type";
import { useGetUserQuery } from "services/users";
import { getPathNameWithoutEndingSlash } from "utils/common";
import { ensureHtmlLineBreaks } from "utils/format-compat";
import { promiseWithTimeout, TimeoutError } from "utils/promise.util";
import { formatReportInput } from "utils/reports.utils";
import { safeRefetch } from "utils/services.util";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import { buildDefaultNewReportConfig } from "./build-default-new-report-config.util";
import { RequiredReportValues } from "./patient-audiology-report/required-report-values.type";
import { upsertReportSetting } from "./report-local-settings/report-local-settings-db.util";
import { UpdateReportValues } from "./update-report-values.type";
import {
  buildReportDefaultUserPreferences,
  buildReportIsSelectedUserPreferences,
  getReportsDefaultsToApply
} from "./utils";

const PatientReportsContext = React.createContext<any>(null);

type PatientReportsProviderProps = {
  children?: React.ReactNode;
  embedded?: boolean;
  scopedAppointmentId?: number;
  initialReportId?: number | null;
};

const NO_PATIENT_ID = -1;

// The editor's page-wide spinner waits on `applyDefaultReportSettings`, whose defaults
// read goes to IndexedDB. That read was seen to never settle while an Ambient Scribe
// recording wrote to IndexedDB in another tab (SN-20000), so past this the open skips
// the defaults instead of waiting.
export const REPORT_DEFAULTS_READ_TIMEOUT_MS = 3000;

const localAllowSignatureToOverlap = setupLocalStorageAPI(
  "allowSignatureToOverlap"
);

export function PatientReportsProvider({
  children,
  embedded = false,
  scopedAppointmentId,
  initialReportId = null
}: PatientReportsProviderProps) {
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const params = useParams();

  const location = useLocation();

  const isMultipageReportEnabled = flags[MULTI_PAGE_AUDIOLOGY_REPORT];
  const scopedReportTags = Boolean(flags[AUDIOLOGY_REPORT_DETAIL_TAG_SCOPE]);
  const isNoahSessionPickerEnabled = Boolean(
    flags[NOAH_SESSION_PICKER_NEW_REPORT]
  );
  const { patientId = "" } = params;

  const prevLocation = useRef(location);
  const isAudiologySection =
    embedded ||
    location.pathname.startsWith(`/patients/${patientId}/audiology`);

  const { noahNotification, setNoahNotification } = useClinicNotifications();
  const { clearCompletionChunks } = useClinicNotifications();

  const [currentReportId, setCurrentReportId] = useState<number | null>(
    initialReportId
  );

  useEffect(() => {
    if (initialReportId != null) {
      setCurrentReportId(initialReportId);
    }
  }, [initialReportId]);
  // Lazy-initialize from the URL so the first render already has the correct
  // patient id. Without this, the first render passes patientId=-1 to the
  // shared usePagination hook, whose internal currentParams state then lags by
  // one render and causes a real /reports/?patient=-1 request to fire on the
  // very next render once skip flips to false.
  const [currentPatientId, setCurrentPatientId] = useState<number>(() =>
    patientId ? Number.parseInt(patientId) : NO_PATIENT_ID
  );
  const [updateReportMutation, { isLoading: isUpdating }] =
    useUpdateReportMutation();
  const [addReportMutation, { isLoading: isAddingReport }] =
    useAddReportMutation();
  const [addCustomTestMutation, { isLoading: isAddingCustomTest }] =
    useAddCustomTestMutation();
  const [removeCustomTestMutation, { isLoading: isRemovingCustomTest }] =
    useRemoveCustomTestMutation();
  const [updateCustomTestMutation, { isLoading: isUpdatingCustomTest }] =
    useUpdateCustomTestMutation();
  const [autocompleteSummary] = useAutoCompleteReportSummaryMutation();

  const [allowSignatureToOverlap, setAllowSignatureToOverlap] = useState(
    localAllowSignatureToOverlap.getData(true)
  );

  const updateAllowSignatureToOverlap = useCallback((value: boolean) => {
    setAllowSignatureToOverlap(value);
    localAllowSignatureToOverlap.updateData(value);
  }, []);

  useEffect(() => {
    setCurrentPatientId(patientId ? Number.parseInt(patientId) : NO_PATIENT_ID);
  }, [patientId]);

  const {
    refetch,
    isFetching,
    data: reports,
    isUninitialized,
    onScroll: onReportsScroll,
    clearData
  } = usePagination<ReportFilters, Report>({
    useQuery: useGetReportsQuery,
    limit: isAudiologySection ? 25 : 1,
    options: {
      skip:
        !Boolean(currentPatientId) ||
        currentPatientId === NO_PATIENT_ID ||
        (embedded && !scopedAppointmentId)
    },
    params: {
      patientId: currentPatientId,
      appointmentId: embedded ? scopedAppointmentId : undefined,
      type: !isMultipageReportEnabled ? REPORT_TYPE_AUDIOLOGY : undefined,
      query: withNoahSessionFields(
        GET_LIST_OF_REPORTS_DEFAULT_QUERY,
        isNoahSessionPickerEnabled,
        REPORT_LIST_NOAH_SESSION_FIELDS
      ),
      ordering: "-report_date,-pk"
    }
  });

  const latestReportId = reports?.at(0)?.id;
  const [
    getLatestReport,
    {
      data: latestFetchedReport,
      isFetching: isLatestReportFetching,
      isUninitialized: isLatestReportUninitialized
    }
  ] = useLazyGetReportQuery();

  const {
    data: latestFetchedAudiograms,
    isFetching: isLatestAudiogramFetching,
    isUninitialized: isLatestAudiogramUninitialized
  } = useGetReportsQuery(
    {
      patientId: currentPatientId,
      limit: 1,
      type: REPORT_TYPE_AUDIOLOGY,
      has_audiogram: true,
      query: GET_REPORT_DEFAULT_QUERY,
      ordering: "-report_date,-pk"
    },
    {
      skip: !Boolean(currentPatientId) || currentPatientId === NO_PATIENT_ID
    }
  );

  useEffect(() => {
    if (latestReportId) {
      getLatestReport({
        reportId: Number(latestReportId),
        query: GET_REPORT_DEFAULT_QUERY,
        scopedTags: scopedReportTags
      });
    }
  }, [latestReportId, getLatestReport, scopedReportTags]);

  const latestAudiogram = useMemo(() => {
    return Boolean(currentPatientId) && currentPatientId !== NO_PATIENT_ID
      ? latestFetchedAudiograms?.at(0)
      : undefined;
  }, [latestFetchedAudiograms, currentPatientId]);

  const latestReport = latestReportId ? latestFetchedReport : undefined;

  const defaultNewReportConfig = useMemo(
    () => buildDefaultNewReportConfig(latestReport),
    [latestReport]
  );

  const isLoadingReports = isFetching || isUninitialized;
  const isUpdatingReport = [
    isUpdating,
    isAddingReport,
    isAddingCustomTest,
    isRemovingCustomTest,
    isUpdatingCustomTest
  ].some(Boolean);

  const {
    data: report,
    isError: reportHaveError,
    isFetching: isFetchingReport,
    isUninitialized: isUninitializedReport,
    refetch: refetchGetReport
  } = useGetReportQuery(
    {
      reportId: Number(currentReportId),
      query: withNoahSessionFields(
        GET_REPORT_DEFAULT_QUERY,
        isNoahSessionPickerEnabled,
        REPORT_NOAH_SESSION_FIELDS
      ),
      scopedTags: scopedReportTags
    },
    { skip: !currentReportId }
  );

  const [syncReportFromNoah, { isLoading: isSyncingFromNoah }] =
    useSyncReportFromNoahMutation();

  const {
    data: customTests,
    refetch: refetchCustomTests,
    isUninitialized: isCustomTestsUninitialized
  } = useGetReportCustomTestsQuery(currentReportId!, {
    skip: !currentReportId || !isAudiologySection
  });

  const safeRefetchCustomTests = useCallback(
    () => safeRefetch(refetchCustomTests, isCustomTestsUninitialized),
    [refetchCustomTests, isCustomTestsUninitialized]
  );

  const isLoadingReport =
    Boolean(currentReportId) &&
    (isFetchingReport ||
      isUninitializedReport ||
      (isAudiologySection && isCustomTestsUninitialized));

  const createReport = useCallback(
    (reportData: RequiredReportValues) => {
      const report = formatReportInput(reportData);
      return addReportMutation(report).then((report) => {
        if ("data" in report && report.data.id) {
          return report.data;
        } else if ("error" in report) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={report} />)
          );
          dispatch(setOpen(true));
          return;
        }
        return;
      });
    },
    [addReportMutation, dispatch]
  );
  const addCustomTest = useCallback(
    ({
      id,
      title,
      description,
      isSelected
    }: {
      id?: number;
      title: string;
      description: string;
      isSelected: boolean;
    }) => {
      if (typeof currentReportId !== "number") {
        return;
      }
      let customTestMutation: any = addCustomTestMutation;
      const customTestData: CustomTestInput = {
        id,
        name: title,
        details: description,
        is_selected: isSelected,
        report_id: currentReportId
      };
      if (id) {
        customTestMutation = updateCustomTestMutation;
      }
      return customTestMutation(customTestData).then(() => {
        safeRefetchCustomTests();
      });
    },
    [
      addCustomTestMutation,
      updateCustomTestMutation,
      currentReportId,
      safeRefetchCustomTests
    ]
  );
  const removeCustomTest = useCallback(
    (id?: number) => {
      if (typeof currentReportId !== "number") {
        return;
      }
      if (id === undefined) {
        return safeRefetchCustomTests();
      }
      return removeCustomTestMutation({ id, report_id: currentReportId }).then(
        () => {
          safeRefetchCustomTests();
        }
      );
    },
    [removeCustomTestMutation, currentReportId, safeRefetchCustomTests]
  );
  // RTK's `refetch()` throws synchronously ("Cannot refetch a query that has
  // not been started yet", minified #38) once the hook's subscription is gone:
  // the provider unmounted, `skip` flipped, or the store was reset while an
  // autosave / default-settings PATCH was still in flight. Every caller runs
  // this from a `.then`, so that throw surfaced as an unhandled rejection in
  // RUM (~30 a day, spikes of 300). `safeRefetch` turns it into a no-op.
  const refetchReport = useCallback((): Promise<Report | undefined> => {
    if (isUninitializedReport) {
      return Promise.resolve(undefined);
    }
    dispatch(reportsApi.util.invalidateTags(["CustomTests"]));
    dispatch(mergeTagsApi.util.invalidateTags(["MergeTagsReport"]));
    return safeRefetch(
      () => refetchGetReport().unwrap(),
      isUninitializedReport
    ).then((data) => {
      if (data) {
        clearData();
      }
      return data || undefined;
    });
  }, [dispatch, isUninitializedReport, refetchGetReport, clearData]);

  const setReportId = useCallback(
    (reportId: string, refetch: boolean = false) => {
      const newReportId = Boolean(reportId) ? Number.parseInt(reportId) : null;
      setCurrentReportId(newReportId);
      setDefaultSettingsAppliedForReportId(undefined);

      if (
        newReportId &&
        refetch &&
        newReportId !== currentReportId &&
        !report?.isSigned
      ) {
        setTimeout(refetchReport, 1);
      }
    },
    [currentReportId, refetchReport, report?.isSigned]
  );

  // Every autosaving section on this page saves through `updateReport`, i.e. the same
  // `PATCH /api/reports/<id>/` against the same row. The backend holds
  // `select_for_update` on that row for the whole request, so concurrent section saves
  // queue on a row lock instead of merging — one gunicorn worker held per waiting
  // section. Passing this as `serializeKey` to `useFormAutoSave` makes them take turns.
  // Derived here, where `currentReportId` lives, so the eleven call sites cannot drift
  // apart from each other (ENG-1849).
  const reportSaveKey = currentReportId
    ? `report-${currentReportId}`
    : undefined;

  const updateReport = useCallback(
    (reportData: UpdateReportValues) => {
      const report = formatReportInput(reportData);
      return updateReportMutation({
        id: Number(currentReportId),
        ...report
      }).then((response) => {
        if (!("error" in response)) {
          dispatch(mergeTagsApi.util.invalidateTags(["MergeTagsReport"]));
        }
        return response;
      });
    },
    [updateReportMutation, currentReportId, dispatch]
  );

  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: user } = useGetUserQuery(currentUser.id, {
    skip: !currentUser.id || currentUser.id === -1 || !isAudiologySection
  });

  const [
    defaultSettingsAppliedForReportId,
    setDefaultSettingsAppliedForReportId
  ] = useState<number | undefined>(undefined);

  // Reports whose defaults read was skipped. A later call for the same report applies
  // nothing even if its own read succeeds, so a skip never turns into a PATCH (and the
  // refetch after it) once the editor is open.
  const skippedReportIdsRef = useRef<Set<number>>(new Set());

  const applyUserPreferencesToReport = useCallback(async () => {
    if (!currentReportId || !report) return;

    const userPreferences = user?.user_preferences?.reporting;

    const {
      reportToUpdate: reportIsSelectedUpdated,
      emitEventsFn: emitEventsFnForIsSelected
    } = buildReportIsSelectedUserPreferences(userPreferences, report);
    emitEventsFnForIsSelected();

    const { reportToUpdate, emitEventsFn } = buildReportDefaultUserPreferences(
      userPreferences,
      report,
      reportIsSelectedUpdated
    );

    //run PATCH request to save default values
    await updateReportMutation({
      id: currentReportId,
      ...reportToUpdate
    });
    emitEventsFn();
  }, [
    currentReportId,
    report,
    updateReportMutation,
    user?.user_preferences?.reporting
  ]);

  const applyDefaultReportSettings = useCallback(async () => {
    if (
      report &&
      report?.type === REPORT_TYPE_AUDIOLOGY &&
      !report?.isSigned &&
      user &&
      currentClinic?.practice?.id &&
      patientId &&
      currentReportId &&
      // `report` keeps the previous report's response while a newly opened one loads,
      // and defaults computed from it must not be PATCHed onto this one.
      Number(report.id) === Number(currentReportId)
    ) {
      const practiceId = currentClinic?.practice?.id;
      const reportId = Number(currentReportId);

      let defaults: Awaited<ReturnType<typeof getReportsDefaultsToApply>>;
      try {
        defaults = await promiseWithTimeout(
          getReportsDefaultsToApply({
            practiceId,
            patientId: Number(patientId),
            reportId,
            user,
            report
          }),
          REPORT_DEFAULTS_READ_TIMEOUT_MS
        );
      } catch (error) {
        // Apply nothing: a read that failed is not "no stored setting". Recorded first,
        // so a sibling call whose read lands later cannot PATCH this report either.
        skippedReportIdsRef.current.add(reportId);
        datadogRum.addAction("audiology_report_defaults_skipped", {
          reason: error instanceof TimeoutError ? "timeout" : "error",
          error_name: error instanceof Error ? error.name : typeof error,
          report_id: reportId
        });
        // Mark the test selection as handled, so a later open whose read succeeds cannot
        // untick tests on a report the clinician has already worked on. Not awaited: on a
        // stalled database this write queues behind the same stall.
        upsertReportSetting({
          practiceId,
          patientId: Number(patientId),
          reportId,
          testsCheckedByDefault: true
        }).catch(() => {});
        return false;
      }

      if (skippedReportIdsRef.current.has(reportId)) {
        return false;
      }

      const { setting, updatedReport, shouldUpdate } = defaults;
      if (shouldUpdate) {
        const response = await updateReportMutation({
          id: reportId,
          ...updatedReport
        });

        if ("data" in response && response.data?.id) {
          // Not awaited: the PATCH has landed, and a stalled bookkeeping write must
          // not hold the editor's spinner.
          upsertReportSetting(setting).catch(() => {});
          return true;
        }
      }
      return false;
    }
    return new Promise((resolve) => resolve(false));
  }, [
    currentClinic?.practice?.id,
    currentReportId,
    patientId,
    report,
    updateReportMutation,
    user
  ]);

  const refetchReports = useCallback(() => {
    dispatch(reportsApi.util.invalidateTags(["Reports"]));
    dispatch(reportsApi.util.invalidateTags(["CustomTests"]));
    dispatch(reportsApi.util.invalidateTags(["ReportAttachments"]));
    dispatch(mergeTagsApi.util.invalidateTags(["MergeTagsReport"]));
    if (!isUninitialized && !isUninitializedReport) {
      clearData();
      safeRefetch(refetch, isUninitialized);
    }

    if (!isLatestReportUninitialized && !isLatestReportFetching) {
      setTimeout(() => {
        getLatestReport({
          reportId: Number(latestReportId),
          query: GET_REPORT_DEFAULT_QUERY,
          scopedTags: scopedReportTags
        });
      }, 1);
    }
  }, [
    dispatch,
    clearData,
    isUninitialized,
    isUninitializedReport,
    isLatestReportUninitialized,
    isLatestReportFetching,
    refetch,
    getLatestReport,
    latestReportId,
    scopedReportTags
  ]);

  useEffect(() => {
    if (noahNotification?.patient_id === Number(patientId)) {
      if (location.pathname === `/patients/${patientId}/noah`) {
        dispatch(setCustomMessage("Noah Session Updated!"));
        dispatch(setOpen(true));
      }

      if (
        noahNotification?.report_id &&
        location.pathname.includes(`/patients/${patientId}/audiology`)
      ) {
        dispatch(setCustomMessage("Audiology Report Updated!"));
        dispatch(setOpen(true));
      }

      refetchReports();
    }

    setNoahNotification(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    noahNotification?.patient_id,
    patientId,
    currentReportId,
    dispatch,
    setNoahNotification
  ]);

  useEffect(() => {
    if (!isAudiologySection || embedded) return;
    const prevUrl = getPathNameWithoutEndingSlash(
      prevLocation.current.pathname
    );
    const nextUrl = getPathNameWithoutEndingSlash(location.pathname);

    if (prevUrl !== nextUrl) {
      if (
        !isLoadingReports &&
        nextUrl !== `/patients/${patientId}/audiology/${currentReportId}`
      ) {
        refetchReports();
      }
      prevLocation.current = location;
    }
  }, [
    location.pathname,
    location,
    refetchReports,
    isLoadingReports,
    patientId,
    currentReportId,
    isAudiologySection,
    embedded
  ]);

  const handleAskAi = useCallback(
    (params: {
      id: string;
      reportId: number;
      prompt: string;
      fieldContent: string;
      lastResult?: string;
      as_soap_note?: boolean;
      format?: PromptOutputFormat;
    }) => {
      const { id, reportId, prompt, lastResult, format } = params;
      clearCompletionChunks();
      dispatch(
        setAiPromptDetails({
          id,
          prompt,
          result: lastResult,
          dialogOpen: true,
          isBusy: true
        })
      );

      return autocompleteSummary({
        report_id: reportId,
        user_prompt: prompt,
        format
      }).then(({ data }: any) => {
        clearCompletionChunks();
        dispatch(
          setAiPromptDetails({
            id,
            prompt,
            result: ensureHtmlLineBreaks(data),
            dialogOpen: true,
            isBusy: false
          })
        );
      });
    },
    [autocompleteSummary, clearCompletionChunks, dispatch]
  );

  const values = useMemo(() => {
    return {
      report,
      latestReport,
      latestAudiogram,
      isLatestReportFetching,
      isLatestAudiogramFetching,
      isLatestReportUninitialized,
      isLatestAudiogramUninitialized,
      customTests,
      reports,
      isLoadingReports,
      isLoadingReport,
      isFetchingReport,
      isUpdatingReport,
      isSyncingFromNoah,
      updateReport,
      reportSaveKey,
      createReport,
      addCustomTest,
      setReportId,
      refetchReports,
      refetchReport,
      removeCustomTest,
      onReportsScroll,
      syncReportFromNoah,
      currentReportId,
      reportHaveError,
      defaultNewReportConfig,
      isUninitializedReport,
      applyDefaultReportSettings,
      defaultSettingsAppliedForReportId,
      setDefaultSettingsAppliedForReportId,
      handleAskAi,
      applyUserPreferencesToReport,
      allowSignatureToOverlap,
      updateAllowSignatureToOverlap
    };
  }, [
    addCustomTest,
    applyDefaultReportSettings,
    createReport,
    currentReportId,
    customTests,
    defaultNewReportConfig,
    defaultSettingsAppliedForReportId,
    handleAskAi,
    isFetchingReport,
    isLatestReportFetching,
    isLatestAudiogramFetching,
    isLatestReportUninitialized,
    isLatestAudiogramUninitialized,
    isLoadingReport,
    isLoadingReports,
    isSyncingFromNoah,
    isUninitializedReport,
    isUpdatingReport,
    latestReport,
    latestAudiogram,
    onReportsScroll,
    refetchReport,
    refetchReports,
    removeCustomTest,
    report,
    reportHaveError,
    reports,
    setReportId,
    syncReportFromNoah,
    updateReport,
    reportSaveKey,
    applyUserPreferencesToReport,
    allowSignatureToOverlap,
    updateAllowSignatureToOverlap
  ]);

  return (
    <PatientReportsContext.Provider value={values}>
      {children}
    </PatientReportsContext.Provider>
  );
}

export default function usePatientReports(): {
  isUninitializedReport: boolean;
  isFetchingReport: boolean;
  report: Report;
  latestReport: Report | undefined;
  latestAudiogram: Report | undefined;
  isLatestReportFetching: boolean;
  isLatestAudiogramFetching: boolean;
  isLatestReportUninitialized: boolean;
  isLatestAudiogramUninitialized: boolean;
  customTests: CustomTest[];
  reports: Report[];
  isLoadingReports: boolean;
  isLoadingReport: boolean;
  isUpdatingReport: boolean;
  isSyncingFromNoah: boolean;
  createReport: (reportData: RequiredReportValues) => Promise<ReportResponse>;
  updateReport: (reportData: UpdateReportValues) => Promise<ReportResponse>;
  /** `serializeKey` for `useFormAutoSave` — shared by every form that PATCHes this report. */
  reportSaveKey: string | undefined;
  syncReportFromNoah: (props: {
    report_id: number;
    session_id?: number;
  }) => Promise<void>;
  setReportId: (reportId: string, refetch?: boolean) => void;
  addCustomTest: ({
    id,
    title,
    description,
    isSelected
  }: {
    id?: number;
    title: string;
    description: string;
    isSelected: boolean;
  }) => Promise<void>;
  removeCustomTest: (id?: number) => Promise<void>;
  refetchReports: () => void;
  refetchReport: () => void;
  onReportsScroll: (event: UIEvent<HTMLDivElement>) => void;
  currentReportId: number | null;
  reportHaveError: boolean;
  defaultNewReportConfig: {
    audiometer: Audiometer | undefined;
    calibrationDate?: Date | null;
    testMethod: number | undefined;
    reliability: number | undefined;
    transducer: number | undefined;
    audiometerConfiguration: AudiometerConfiguration | null;
  };
  applyDefaultReportSettings: () => Promise<boolean>;
  defaultSettingsAppliedForReportId: number | undefined;
  setDefaultSettingsAppliedForReportId: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  handleAskAi: (params: {
    id: string;
    reportId: number;
    prompt: string;
    fieldContent: string;
    lastResult?: string;
    as_soap_note?: boolean;
    format?: PromptOutputFormat;
  }) => Promise<void>;
  applyUserPreferencesToReport: () => Promise<void>;
  allowSignatureToOverlap: boolean;
  updateAllowSignatureToOverlap: (allowSignatureToOverlap: boolean) => void;
} {
  const context = React.useContext(PatientReportsContext);
  if (!context) {
    throw new Error(
      `usePatientReports must be used within a PatientReportsProvider`
    );
  }
  return context;
}
